import { useEffect, useRef, useState } from 'react';
import { normalizeVtt } from '@mlm/shared';
import type { PlayerCaption } from '@mlm/ui';
import { fetchCaptionVtt, type CaptionTrack } from '../lib/api';

/**
 * Turn the API's caption paths into same-origin `blob:` URLs for `<track>`.
 *
 * A cross-origin `<track src>` requires `crossorigin` on the media element,
 * which in turn makes the VIDEO fetch a CORS request — so enabling captions the
 * obvious way would put playback itself at the mercy of a per-tenant R2 bucket
 * policy that is pasted in by hand and today omits both the apex domain and the
 * mobile app's origin. Fetching the VTT ourselves and wrapping it in a blob
 * sidesteps all of it: the blob inherits our origin, so the track loads and the
 * media fetch stays a plain no-cors request.
 *
 * A track that fails to load is dropped rather than surfaced — a missing
 * subtitle must never interrupt the lesson.
 */
export function useLessonCaptions(captions: CaptionTrack[] | undefined): PlayerCaption[] {
  const [tracks, setTracks] = useState<PlayerCaption[]>([]);
  // Identity of the caption set: a parent re-render hands us a fresh array with
  // the same contents, and re-fetching on that would churn blob URLs mid-lesson.
  const key = (captions ?? []).map((c) => `${c.id}:${c.src}`).join('|');
  const captionsRef = useRef(captions);
  captionsRef.current = captions;

  useEffect(() => {
    const list = captionsRef.current ?? [];
    if (list.length === 0) {
      setTracks([]);
      return;
    }
    let cancelled = false;
    const urls: string[] = [];
    const controller = new AbortController();

    void Promise.all(
      list.map(async (caption) => {
        try {
          const text = await fetchCaptionVtt(caption.src);
          const vtt = normalizeVtt(text);
          if (!vtt) return null;
          const url = URL.createObjectURL(new Blob([vtt], { type: 'text/vtt' }));
          urls.push(url);
          return { id: caption.id, lang: caption.lang, label: caption.label, blobUrl: url };
        } catch {
          return null;
        }
      }),
    ).then((resolved) => {
      if (cancelled) {
        for (const url of urls) URL.revokeObjectURL(url);
        return;
      }
      setTracks(resolved.filter((t): t is PlayerCaption => t !== null));
    });

    return () => {
      cancelled = true;
      controller.abort();
      for (const url of urls) URL.revokeObjectURL(url);
    };
  }, [key]);

  return tracks;
}
